import { useState } from "react";
import axios from "axios";
import emailjs from '@emailjs/browser';
import { useSearchParams } from "react-router-dom";
import { MapPin, Phone, Mail, Globe, Smile, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { PRODUCTS } from "../data/products";
import { Reveal } from "../components/Reveal";
import { SITE, waLink } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [params] = useSearchParams();
  const preselect = params.get("product") || "";

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    location: "",
    product: preselect,
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setStatus({ state: "error", msg: "Name and Phone are required." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/inquiries`, { ...form, source: "contact_page" });
      setStatus({ state: "success", msg: "Inquiry submitted! We'll get back to you soon." });
      setForm({ name: "", phone: "", email: "", location: "", product: "", message: "" });
    } catch (err) {
      console.error(err?.response || err);
      const msg = err?.response?.data?.message || "Could not submit. Please try WhatsApp or call us.";
      setStatus({ state: "error", msg });
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();

    if (!form.name.trim() || !form.phone.trim()) {
      setStatus({ state: "error", msg: "Name and Phone are required." });
      return;
    }

    setStatus({ state: "loading", msg: "" });

    let backendOk = false;
    try {
      await axios.post(`${API}/inquiries`, { ...form, source: "contact_page" });
      backendOk = true;
    } catch (err) {
      console.error("Backend submit failed:", err?.response || err);
    }

    let emailOk = false;
    try {
      const templateParams = {
        name: form.name,
        phone: form.phone,
        email: form.email,
        city: form.location,
        product: form.product,
        message: form.message,
        time: new Date().toLocaleString(),
      };
      
      const serviceId = "service_xcayefl";
      const templateId = "template_39kezfg";
      const publicKey = "xVBNUVsbGBBfHe8ss";
      
      await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );
      emailOk = true;
    } catch (error) {
      console.error("EmailJS send failed:", error);
    }

    if (backendOk || emailOk) {
      setStatus({ state: "success", msg: "Inquiry submitted successfully." });
      setForm({ name: "", phone: "", email: "", location: "", product: "", message: "" });
    } else {
      setStatus({ state: "error", msg: "Could not submit. Please try WhatsApp or call us." });
    }
  };

  return (
    <div data-testid="page-contact">
      {/* Header */}
      <section className="bg-brand-surface border-b border-brand-line py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-brand-orange font-heading font-bold text-xs tracking-[0.22em] uppercase mb-3">Contact Us</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-brand-ink tracking-tight mb-3">Get in Touch</h1>
          <p className="text-base text-brand-ink/65 max-w-2xl">Have a question, need product guidance or want to place a bulk order? Our team is here to help.</p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Info Cards */}
          <div className="lg:col-span-2 space-y-4">
            <Reveal>
              <div className="bg-brand-surface border border-brand-line rounded-lg p-6 flex items-start gap-4">
                <div className="w-11 h-11 rounded-md bg-brand-orange/10 text-brand-orange flex items-center justify-center shrink-0"><MapPin size={20} /></div>
                <div>
                  <div className="font-heading font-bold text-sm uppercase tracking-wide text-brand-ink mb-1">Visit Us</div>
                  <p className="text-sm text-brand-ink/75 leading-relaxed">{SITE.address}</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={60}>
              <a href={`tel:${SITE.phoneRaw}`} data-testid="contact-call-card" className="block bg-brand-surface border border-brand-line rounded-lg p-6 hover:border-brand-orange transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-md bg-brand-orange/10 text-brand-orange flex items-center justify-center shrink-0"><Phone size={20} /></div>
                  <div>
                    <div className="font-heading font-bold text-sm uppercase tracking-wide text-brand-ink mb-1">Call Us</div>
                    <p className="text-sm text-brand-ink/75">{SITE.phoneDisplay}</p>
                  </div>
                </div>
              </a>
            </Reveal>
            <Reveal delay={120}>
              <a href={`mailto:${SITE.email}`} data-testid="contact-email-card" className="block bg-brand-surface border border-brand-line rounded-lg p-6 hover:border-brand-orange transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-md bg-brand-orange/10 text-brand-orange flex items-center justify-center shrink-0"><Mail size={20} /></div>
                  <div>
                    <div className="font-heading font-bold text-sm uppercase tracking-wide text-brand-ink mb-1">Email Us</div>
                    <p className="text-sm text-brand-ink/75 break-all">{SITE.email}</p>
                  </div>
                </div>
              </a>
            </Reveal>
            {/* <Reveal delay={180}>
              <div className="bg-brand-surface border border-brand-line rounded-lg p-6 flex items-start gap-4">
                <div className="w-11 h-11 rounded-md bg-brand-orange/10 text-brand-orange flex items-center justify-center shrink-0"><Globe size={20} /></div>
                <div>
                  <div className="font-heading font-bold text-sm uppercase tracking-wide text-brand-ink mb-1">Website</div>
                  <p className="text-sm text-brand-ink/75">{SITE.domain}</p>
                </div>
              </div>
            </Reveal> */}

            <a
              href={waLink('Hello Desire Vet Care, I have an inquiry.')}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white font-heading font-semibold text-sm px-6 py-4 rounded-md transition-colors"
            >
              <Smile size={18} /> Chat on WhatsApp
            </a>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <Reveal>
              <form onSubmit={sendEmail} className="bg-white border border-brand-line rounded-lg p-7 sm:p-9" data-testid="inquiry-form">
                <h2 className="font-heading font-extrabold text-2xl text-brand-ink mb-1 tracking-tight">Send us an Inquiry</h2>
                <p className="text-sm text-brand-ink/60 mb-6">Fill in the form and our team will reach out within 24 hours.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-heading font-semibold uppercase tracking-wider text-brand-ink/70 mb-1.5">Name *</label>
                    <input data-testid="contact-name" type="text" value={form.name} onChange={change("name")} required className="w-full px-4 py-3 rounded-md border border-brand-line bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange" placeholder="Your full name" />
                  </div>
                  <div>
                    <label className="block text-xs font-heading font-semibold uppercase tracking-wider text-brand-ink/70 mb-1.5">Phone *</label>
                    <input data-testid="contact-phone" type="tel" value={form.phone} onChange={change("phone")} required className="w-full px-4 py-3 rounded-md border border-brand-line bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange" placeholder="+91 9XXXXXXXXX" />
                  </div>
                  <div>
                    <label className="block text-xs font-heading font-semibold uppercase tracking-wider text-brand-ink/70 mb-1.5">Email</label>
                    <input data-testid="contact-email" type="email" value={form.email} onChange={change("email")} className="w-full px-4 py-3 rounded-md border border-brand-line bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange" placeholder="you@example.com" />
                  </div>
                  <div>
                    <label className="block text-xs font-heading font-semibold uppercase tracking-wider text-brand-ink/70 mb-1.5">State / City</label>
                    <input data-testid="contact-location" type="text" value={form.location} onChange={change("location")} className="w-full px-4 py-3 rounded-md border border-brand-line bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange" placeholder="e.g. Punjab, Ludhiana" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-heading font-semibold uppercase tracking-wider text-brand-ink/70 mb-1.5">Product Interested In</label>
                    <select data-testid="contact-product" value={form.product} onChange={change("product")} className="w-full px-4 py-3 rounded-md border border-brand-line bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange">
                      <option value="">-- Select a product (optional) --</option>
                      {PRODUCTS.map((p) => <option key={p.slug} value={p.name}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-heading font-semibold uppercase tracking-wider text-brand-ink/70 mb-1.5">Message</label>
                    <textarea data-testid="contact-message" value={form.message} onChange={change("message")} rows={4} className="w-full px-4 py-3 rounded-md border border-brand-line bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange resize-none" placeholder="Tell us how we can help..." />
                  </div>
                </div>

                {status.state === "success" && (
                  <div data-testid="contact-success" className="mt-5 flex items-start gap-2 p-3 rounded-md bg-brand-green/10 border border-brand-green/30 text-brand-green text-sm">
                    <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> {status.msg}
                  </div>
                )}
                {status.state === "error" && (
                  <div data-testid="contact-error" className="mt-5 flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                    <AlertCircle size={18} className="mt-0.5 shrink-0" /> {status.msg}
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={status.state === "loading"}
                  className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeDark text-white font-heading font-semibold text-sm px-7 py-3.5 rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status.state === "loading" ? "Submitting..." : (<>Submit Inquiry <Send size={16} /></>)}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Map */}
      {/* <section className="bg-brand-surface">
        <div className="mx-auto">
          <div className="rounded-lg overflow-hidden border border-brand-line bg-white" data-testid="contact-map">
            <iframe
              title="Desire Vet Care location"
              src={SITE.mapsEmbed}
              loading="lazy"
              className="w-full h-[360px]"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section> */}
    </div>
  );
}
